import CourseButtons from '@components/CourseButtons'
import CourseCurriculum from '@components/CourseCurriculum'
import Divider from '@components/Divider'
import { ScrollAnimation } from '@components/ScrollAnimation'
import { css, useTheme } from '@emotion/react'
import styled from '@emotion/styled'
import { SmaIndexQuery } from '@graphql-types'
import { graphql, PageProps } from 'gatsby'
import { GatsbyImage } from 'gatsby-plugin-image'
import React, { useRef } from 'react'
import { useTranslation } from 'react-i18next'
import ReactMarkdown from 'react-markdown'
import { maxWidth, minWidth, responsiveColumn } from 'src/themes'
import Card from './_card'
import SMALayout from './_layout'
import useScrollSpy from 'react-use-scrollspy'
import StickyBox from "react-sticky-box";

export const H2Highlight = styled.h2`
	color: ${(props) => props.theme.highlightColor};
	text-align: left;
`

export const RoundedGatsbyImage = styled(GatsbyImage)`
	border-radius: 20px;
`

const SMAIndexPage = (props: PageProps<SmaIndexQuery>) => {
  const { t } = useTranslation()
  const theme = useTheme()

  const sectionRefs = [
    useRef(null),
    useRef(null),
    useRef(null),
    useRef(null),
    // useRef(null),
  ]

  const activeSection = useScrollSpy({
    sectionElementRefs: sectionRefs,
    offsetPx: -200,
  })

  const course = props.data.course!
  return (
    <SMALayout course={course}>
      <RoundedGatsbyImage
        image={props.data.banner?.childImageSharp?.gatsbyImageData}
        alt={t('courses:sma.title')}
      />
      <div
        css={css`
					margin-top: 2rem;
					display: flex;

					${responsiveColumn};

          a.anchor {
            display: block;
            position: relative;
            top: -100px;
            visibility: hidden;
          }
				`}>
        <div
          css={css`
						width: 360px;

						${maxWidth[2]} {
							width: 100%;
						}

						order: 1;
						color: ${theme.accentColor};
					`}>
          <StickyBox offsetTop={16} offsetBottom={16}>
            <div
              ref={sectionRefs[4]}
              css={css`
                border-radius: 25px;
                margin-left: 20px;
                padding: 25px;
                background-color: #ffffff;
                z-index: 8;
                ${maxWidth[2]} {
                  margin-left: 0;
                }
              `}>
              <a className='anchor' id='details' />
              <div
                css={css`
                  margin: 0 0 1rem 0;
                `}>
                <CourseButtons
                  vertical
                  course={course}
                  openDay={props.data.openDayEvent!}
                  trial={props.data.trialLessonEvent!}
                />
              </div>

              <p
                css={css`
                  margin: 0;
                  font-weight: 900;
                `}>
                {t('courses:sma.course_include')}
              </p>
              <div
                css={css`
                  font-size: 0.8rem;
                  line-height: 1.2rem;
                `}>
                <ReactMarkdown
                  children={t('courses:sma.outline')}
                />
              </div>
            </div>
          </StickyBox>
        </div>
        <div
          css={css`
						max-width: 700px;
						${maxWidth[2]} {
							> :last-child {
								margin-bottom: 11rem;
							}
							padding-bottom: 1rem;
						}
					`}>
          <ReactMarkdown children={course.description!} />
          <div
            css={css`
							display: flex;
							position: sticky;
							top: 1rem;
							justify-content: flex-start;
							z-index: 8;

							a {
								padding: 8px 30px;
								border-radius: 25px;
								margin-right: 20px;
								background-color: #ffffff;
								color: ${theme.accentColor};
							}

							a.active {
								background-color: ${theme.buttonColor};
								color: #ffffff;
							}
							a:last-child {
								display: none;
							}

							${maxWidth[2]} {
								justify-content: center;
								flex-wrap: wrap;
								top: calc(100vh - 10rem);
								gap: 0.5rem;
								margin: -1rem;
								padding: 1rem;
								background: rgba(255, 255, 255, 0.5);

								a {
									font-size: 0.8rem;
									padding: 4px 10px;
									margin-right: 0px;
									width: 40%;
									display: block;
									box-sizing: border-box;
									text-align: center;
								}
								a:last-child {
									display: block;
								}
							}
						`}>
            <a
              className={activeSection === 0 ? 'active' : ''}
              href='#introduction'>
              {t('courses:sma.course_introduction')}
            </a>
            <a
              className={activeSection === 1 ? 'active' : ''}
              href='#content'>
              {t('courses:sma.course_content')}
            </a>
            <a
              className={activeSection === 2 ? 'active' : ''}
              href='#mode'>
              {t('courses:sma.course_mode')}
            </a>
            {/* <a href='#fee'>{t('courses:sma.course_fee')}</a> */}
            <a
              className={activeSection === 3 ? 'active' : ''}
              href='#faq'>
              {t('courses:sma.course_faq')}
            </a>
            <a
              className={activeSection === 4 ? 'active' : ''}
              href='#details'>
              {t('courses:sma.course_details')}
            </a>
          </div>
          <a className='anchor' id='introduction' />
          <H2Highlight>
            {t('courses:sma.course_introduction')}
          </H2Highlight>
          <div
            ref={sectionRefs[0]}
            css={css`
							display: flex;
							${responsiveColumn};
						`}>
            <Card
              icon={require('@images/sma-icons/clock.png')}
              title={t('courses:sma.cards.duration.title')}
              description={t(
                'courses:sma.cards.duration.description'
              )}
            />
            <Card
              icon={require('@images/sma-icons/vector.png')}
              title={t(
                'courses:sma.cards.teaching_language.title'
              )}
              description={t(
                'courses:sma.cards.teaching_language.description'
              )}
            />
          </div>
          <Card
            icon={require('@images/sma-icons/calendar.png')}
            title={t('courses:sma.cards.start_date.title')}
            description={t(
              'courses:sma.cards.start_date.description'
            )}
          />
          <Card
            icon={require('@images/sma-icons/tag.png')}
            title={t('courses:sma.cards.topics.title')}
            description={t('courses:sma.cards.topics.description')}
          />
          <Card
            icon={require('@images/sma-icons/smile.png')}
            title={t('courses:sma.cards.benefits.title')}
            description={t(
              'courses:sma.cards.benefits.description'
            )}
          />
          <Divider />
          <RoundedGatsbyImage
            image={
              props.data.photoIntroduction?.childImageSharp
                ?.gatsbyImageData
            }
            alt={t('courses:sma.photoIntroduction')}
          />
          <div ref={sectionRefs[1]}>
            <a className='anchor' id='content' />
            <H2Highlight>
              {t('courses:sma.course_content')}
            </H2Highlight>

            <div>
              {course.details?.map(
                (detail, i) =>
                  detail && (
                    <CourseCurriculum
                      key={i}
                      noSwapping
                      noImage
                      css={css`
												width: auto;
											`}>
                      <ScrollAnimation
                        animateIn={
                          i % 2 === 0
                            ? 'fadeInRight'
                            : 'fadeInLeft'
                        }>
                        <RoundedGatsbyImage
                          image={
                            detail.imageSharp
                              ?.childImageSharp
                              ?.gatsbyImageData
                          }
                          aria-hidden={true}
                          alt={detail.title!}
                        />
                      </ScrollAnimation>
                      <div>
                        <h3
                          css={css`
														display: flex;
														align-items: center;

														img {
															margin-right: 0.5rem;
														}
													`}>
                          <img
                            src={
                              require('@images/sma-icons/note.png')
                                .default
                            }
                          />
                          {detail.title}
                        </h3>
                        <small
                          css={css`
														color: ${theme.highlightColor};
													`}>
                          {detail.mode}
                        </small>
                        <ReactMarkdown
                          children={
                            detail.description!
                          }
                        />
                        <div className='logos'>
                          {detail.iconsSharp?.map(
                            (icon, j) => (
                              <ScrollAnimation
                                animateIn={
                                  'fadeInUp'
                                }>
                                <GatsbyImage
                                  image={
                                    icon
                                      .childImageSharp
                                      ?.gatsbyImageData
                                  }
                                  alt={
                                    detail
                                      .icons?.[
                                      j
                                    ]?.meta
                                      .title
                                  }
                                />
                              </ScrollAnimation>
                            )
                          )}
                        </div>
                      </div>
                    </CourseCurriculum>
                  )
              )}
            </div>
          </div>
          <Divider />
          <div ref={sectionRefs[2]}>
            <a className='anchor' id='mode' />
            <H2Highlight>
              {t('courses:sma.course_mode')}
            </H2Highlight>

            <div
              css={css`
								display: flex;
								gap: 1rem;
								margin: 1rem 0;
							`}>
              <RoundedGatsbyImage
                image={
                  props.data.photoCourseMode1?.childImageSharp
                    ?.gatsbyImageData
                }
                alt={t(
					'courses:sma.learning_Arrangement'
				  )}
              />
              <RoundedGatsbyImage
                image={
                  props.data.photoCourseMode2?.childImageSharp
                    ?.gatsbyImageData
                }
                alt={t(
					'courses:sma.learning_Arrangement'
				  )}
              />
            </div>
            <Card
              icon={require('@images/sma-icons/book.png')}
              title={t('courses:sma.cards.arrangement.title')}
              description={t(
                'courses:sma.cards.arrangement.description'
              )}
            />
            <div
              css={css`
								display: flex;
								gap: 1rem;
								margin: 1rem 0;
							`}>
              <RoundedGatsbyImage
                image={
                  props.data.photoCourseMode3?.childImageSharp
                    ?.gatsbyImageData
                }
                alt={t(
					'courses:sma.project_Grouping'
				  )}
              />
              <RoundedGatsbyImage
                image={
                  props.data.photoCourseMode4?.childImageSharp
                    ?.gatsbyImageData
                }
                alt={t(
					'courses:sma.project_Grouping'
				  )}
              />
            </div>
            <Card
              icon={require('@images/sma-icons/group.png')}
              title={t('courses:sma.cards.grouping.title')}
              description={t(
                'courses:sma.cards.grouping.description'
              )}
            />
          </div>
          <Divider />
		  <H2Highlight>
              {t('courses:sma.course_cert')}
            </H2Highlight>
		  <RoundedGatsbyImage
            image={
              props.data.TeckyAcademyMicroMasterCertificate?.childImageSharp
                ?.gatsbyImageData
            }
            alt={t('courses:common.tecky_cert')}
          />
          <Divider />
          <div ref={sectionRefs[3]}></div>
          <div ref={sectionRefs[3]}>
            <a className='anchor' id='faq' />
            <H2Highlight>{t('courses:sma.course_faq')}</H2Highlight>
            {props.data.faqs.nodes.map((faq) => (
              <div
                key={faq!.question}
                css={css`
									margin-bottom: 2.45rem;
									width: 100%;

									h3 {
										font-weight: 500;
										padding-bottom: 0.25rem;
										margin-bottom: 0.25rem;
										color: #ffcc00;
									}
								`}>
                <h3>{faq!.question}</h3>
                <p>{faq!.answer}</p>
              </div>
            ))}
          </div>
        </div>
      </div>
    </SMALayout>
  )
}

export default SMAIndexPage

export const query = graphql`
	fragment SMACoursePageFragment on Query {
		openDayEvent: eventbrite(
			status: { eq: "live" }
			name: { text: { regex: "/(Open Day|開放)/" } }
			listed: { eq: true }
		) {
			id
			url
			start {
				utc
			}
		}
		openDayEventUK: eventbrite(
			status: { eq: "live" }
			name: { text: { regex: "/(Open Day|開放)/" } }
			listed: { eq: true }
		) {
			id
			url
			start {
				utc
			}
		}
		trialLessonEvent: eventbrite(
			status: { eq: "live" }
			name: { text: { regex: "/香港.*試堂/" } }
			listed: { eq: true }
		) {
			id
			url
			name {
				text
			}
			start {
				utc
			}
		}
		trialLessonEventUK: eventbrite(
			status: { eq: "live" }
			name: { text: { regex: "/英國.*試堂/" } }
			listed: { eq: true }
		) {
			id
			url
			name {
				text
			}
			start {
				utc
			}
		}
		course: cockpitCoursesCollection(
			_lang: { eq: $language }
			code: { eq: "SMA" }
		) {
			name
			mode
			code
			zohoCode
			leadValue
			slogan
			description
			outcome
			standard_price
			summary {
				class_size_uk
				location_uk
				duration_uk
				time_uk
				class_size
				location
				duration
				time
			}
			image {
				path
			}
			imageSharp {
				childImageSharp {
					gatsbyImageData(width: 500, placeholder: BLURRED)
				}
			}
			details {
				title
				mode
				description
				icons {
					meta
				}
				iconsSharp {
					childImageSharp {
						gatsbyImageData(height: 60, placeholder: BLURRED)
					}
				}
				image {
					path
				}
				imageSharp {
					childImageSharp {
						gatsbyImageData(
							transformOptions: { fit: CONTAIN }
							backgroundColor: "transparent"
							height: 200
							width: 200
							placeholder: BLURRED
						)
					}
				}
			}
			leaflet_available
			cohorts {
				value {
					cohort
					price
					price_valid_until
					price_uk
					price_ca
					available_countries
					kowloon_bay
					kowloon_bay_full
					kowloon_bay_almost
					sheung_wan
					sheung_wan_almost
					sheung_wan_full
					start_date
					tsuen_wan
					tsuen_wan_almost
					tsuen_wan_full
					uk_online
					uk_online_almost
					uk_online_full
					london
					london_almost
					london_full
					ca_online
					ca_online_almost
					ca_online_full
					toronto
					toronto_almost
					toronto_full
				}
			}
			instructors {
				name
				description
				title
				specialities
				profileImg {
					path
				}
				available_countries
				profileImgSharp {
					childImageSharp {
						gatsbyImageData(width: 80, placeholder: BLURRED)
					}
				}
			}
		}
	}
	query SMAIndex($language: String!) {
		...TranslationFragment
		...SMACoursePageFragment
		allCockpitMediaLogosCollection(filter: { _lang: { eq: $language } }) {
			nodes {
				title
				mediaLogo {
					path
				}
				mediaLogoSharp {
					childImageSharp {
						gatsbyImageData(width: 80, placeholder: BLURRED)
					}
				}
			}
		}
		tqukLogo: file(relativePath: { eq: "tquk.png" }) {
			childImageSharp {
				gatsbyImageData(width: 100, placeholder: BLURRED)
			}
		}
		photoIntroduction: file(
			relativePath: { eq: "sma-photos/DSC01617.JPG" }
		) {
			childImageSharp {
				gatsbyImageData(width: 700, placeholder: BLURRED)
			}
		}
		photoCourseMode1: file(
			relativePath: { eq: "sma-photos/DSC05832.JPG" }
		) {
			childImageSharp {
				gatsbyImageData(width: 350, placeholder: BLURRED)
			}
		}
		photoCourseMode2: file(
			relativePath: { eq: "sma-photos/DSC05682.JPG" }
		) {
			childImageSharp {
				gatsbyImageData(width: 350, placeholder: BLURRED)
			}
		}
		photoCourseMode3: file(
			relativePath: { eq: "sma-photos/DSC05863.JPG" }
		) {
			childImageSharp {
				gatsbyImageData(width: 350, placeholder: BLURRED)
			}
		}
		photoCourseMode4: file(
			relativePath: { eq: "sma-photos/DSC06240.JPG" }
		) {
			childImageSharp {
				gatsbyImageData(width: 350, placeholder: BLURRED)
			}
		}
		TeckyAcademyMicroMasterCertificate: file(
			relativePath: { eq: "sma-photos/Tecky_Academy_MicroMaster_Certificate.png" }
		) {
			childImageSharp {
				gatsbyImageData(width: 700, placeholder: BLURRED)
			}
		}
		banner: file(
			relativePath: { eq: "sma-photos/tec_banner_1120x560.png" }
		) {
			childImageSharp {
				gatsbyImageData(width: 1080, placeholder: BLURRED)
			}
		}
		faqs: allCockpitFaqsmaCollection(filter: { _lang: { eq: $language } }) {
			nodes {
				question
				answer
			}
		}
		hiringPartners: cockpitHiringPartnersCollection(
			_lang: { eq: $language }
		) {
			logos {
				meta
			}
			logosSharp {
				childImageSharp {
					id
					gatsbyImageData(
						width: 80
						height: 80
						placeholder: BLURRED
						backgroundColor: "transparent"
						transformOptions: { fit: CONTAIN }
					)
				}
			}
		}
	}
`